import { useMemo } from 'react';
import * as XLSX from 'xlsx-js-style';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CheckCircle, RefreshCw, Trash2, Sparkles, Download } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/core/components/ui/sheet';
import { Button } from '@/core/components/ui/button';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { cn } from '@/core/lib/utils';
import { analyzeDelta, type SmartSuggestion } from '../../utils/smart-suggestions';
import type { IrregularDeltaItem, DeltaMeterValue, HandleType } from '../../types/data-error-types';

const SURROUND_OPTIONS = [1, 3, 5, 10];

function fmt(v: number | null | undefined, decimals: number): string {
  return v != null ? v.toFixed(decimals) : '—';
}

const columnHelper = createColumnHelper<DeltaMeterValue>();

const columns = [
  columnHelper.accessor('meterValueId', {
    header: 'ID',
    cell: info => <span className="font-mono text-xs">{info.getValue()}</span>,
  }),
  columnHelper.accessor('timestamp', {
    header: 'Timestamp',
    cell: info => <span className="text-xs whitespace-nowrap">{format(info.getValue(), 'dd/MM/yyyy HH:mm:ss')}</span>,
  }),
  columnHelper.accessor('machineTimestamp', {
    header: 'Machine TS',
    cell: info => <span className="text-xs whitespace-nowrap">{format(info.getValue(), 'dd/MM/yyyy HH:mm:ss')}</span>,
  }),
  columnHelper.accessor('readingContext', {
    header: 'Context',
    cell: info => <span className="text-xs">{info.getValue()}</span>,
  }),
  columnHelper.accessor('energy', {
    header: 'Energy (kWh)',
    cell: info => <span className="text-xs font-mono">{fmt(info.getValue(), 3)}</span>,
  }),
  columnHelper.accessor('deltaEnergy', {
    header: 'Δ Energy',
    cell: info => <span className="text-xs font-mono">{fmt(info.getValue(), 3)}</span>,
  }),
  columnHelper.accessor('power', {
    header: 'Power (kW)',
    cell: info => <span className="text-xs font-mono">{fmt(info.getValue(), 3)}</span>,
  }),
  columnHelper.accessor('current', {
    header: 'Current (A)',
    cell: info => <span className="text-xs">{info.getValue() ?? '—'}</span>,
  }),
  columnHelper.accessor('voltage', {
    header: 'Voltage (V)',
    cell: info => <span className="text-xs">{info.getValue() ?? '—'}</span>,
  }),
  columnHelper.accessor('temperature', {
    header: 'Temp (°C)',
    cell: info => <span className="text-xs">{fmt(info.getValue(), 1)}</span>,
  }),
  columnHelper.accessor('tariffName', {
    header: 'Tariff',
    cell: info => <span className="text-xs">{info.getValue() ?? '—'}</span>,
  }),
  columnHelper.accessor('totalCost', {
    header: 'Cost',
    cell: info => <span className="text-xs font-mono">{fmt(info.getValue(), 2)}</span>,
  }),
];

// ── Smart suggestion card ─────────────────────────────────────────────────────

const CONFIDENCE_STYLES: Record<SmartSuggestion['confidence'], { border: string; bg: string; badge: string; label: string }> = {
  high:   { border: 'border-amber-300',  bg: 'bg-amber-50',  badge: 'bg-amber-100 text-amber-800',  label: 'High confidence' },
  medium: { border: 'border-blue-200',   bg: 'bg-blue-50',   badge: 'bg-blue-100 text-blue-800',    label: 'Suggestion' },
  low:    { border: 'border-gray-200',   bg: 'bg-gray-50',   badge: 'bg-gray-100 text-gray-700',    label: 'Possible' },
};

interface SuggestionCardProps {
  suggestion: SmartSuggestion;
  onApply: () => void;
  disabled: boolean;
}

function SuggestionCard({ suggestion, onApply, disabled }: SuggestionCardProps) {
  const s = CONFIDENCE_STYLES[suggestion.confidence];
  return (
    <div className={cn('rounded-lg border px-4 py-3 mx-6 mt-3 mb-0', s.border, s.bg)}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          <span className="text-xs font-semibold text-[#262626]">Smart suggestion</span>
        </div>
        <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium shrink-0', s.badge)}>
          {s.label}
        </span>
      </div>
      <p className="text-sm font-medium text-[#262626] mb-1">{suggestion.title}</p>
      <p className="text-xs text-[#606060] mb-2">{suggestion.reason}</p>
      <ul className="space-y-0.5 mb-3">
        {suggestion.evidence.map((e, i) => (
          <li key={i} className="text-xs text-[#606060] flex items-start gap-1">
            <span className="mt-0.5 shrink-0">•</span>
            <span>{e}</span>
          </li>
        ))}
      </ul>
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          className="h-7 text-xs gap-1.5 border-[#262626] text-[#262626] hover:bg-[#262626] hover:text-white"
          onClick={onApply}
        >
          Apply: {suggestion.action}
        </Button>
      </div>
    </div>
  );
}

// ── Action buttons ────────────────────────────────────────────────────────────

const ACTION_BUTTONS: { type: HandleType; label: string; icon: React.ReactNode; cls: string }[] = [
  { type: 'Approve',     label: 'Approve',     icon: <CheckCircle className="h-3.5 w-3.5" />, cls: 'text-green-700 border-green-200 hover:bg-green-50' },
  { type: 'Replacement', label: 'Replacement', icon: <RefreshCw className="h-3.5 w-3.5" />,   cls: 'text-blue-700 border-blue-200 hover:bg-blue-50'   },
  { type: 'Delete',      label: 'Delete',      icon: <Trash2 className="h-3.5 w-3.5" />,      cls: 'text-red-600 border-red-200 hover:bg-red-50'      },
];

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  item: IrregularDeltaItem | null;
  meterValues: DeltaMeterValue[];
  isLoading: boolean;
  error: string | null;
  surroundSize: number;
  handlingId: number | null;
  onClose: () => void;
  onSurroundSizeChange: (n: number) => void;
  onRetry: () => void;
  onHandle: (irregularId: number, type: HandleType) => void;
}

export function MeterValuesBlade({
  open,
  item,
  meterValues,
  isLoading,
  error,
  surroundSize,
  handlingId,
  onClose,
  onSurroundSizeChange,
  onRetry,
  onHandle,
}: Props) {
  const sortedMeterValues = useMemo(
    () => [...meterValues].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
    [meterValues]
  );

  const table = useReactTable({
    data: sortedMeterValues,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const isActioning = item ? handlingId === item.irregularId : false;

  const handleExportMeterValues = () => {
    if (!sortedMeterValues.length || !item) return;
    const rows = sortedMeterValues.map(v => ({
      'Meter Value ID': v.meterValueId,
      'Timestamp': format(v.timestamp, 'dd/MM/yyyy HH:mm:ss'),
      'Machine Timestamp': format(v.machineTimestamp, 'dd/MM/yyyy HH:mm:ss'),
      'Context': v.readingContext,
      'Energy (kWh)': v.energy ?? '',
      'Δ Energy (kWh)': v.deltaEnergy ?? '',
      'Power (kW)': v.power ?? '',
      'Current (A)': v.current ?? '',
      'Voltage (V)': v.voltage ?? '',
      'Temp (°C)': v.temperature ?? '',
      'Tariff': v.tariffName ?? '',
      'Total Cost': v.totalCost ?? '',
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Meter Values');
    XLSX.writeFile(wb, `meter-values-${item.irregularId}-machine-${item.machineId}.xlsx`);
  };
  const visibleActions = item
    ? ACTION_BUTTONS.filter(b => item.handleType !== b.type)
    : ACTION_BUTTONS;

  const suggestion = useMemo(
    () => (!isLoading && item && sortedMeterValues.length > 0 ? analyzeDelta(item, sortedMeterValues) : null),
    [item, sortedMeterValues, isLoading]
  );

  return (
    <Sheet open={open} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto flex flex-col gap-0 p-0">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E8E8E8]">
          <SheetTitle className="text-base font-semibold text-[#262626]">
            {item?.machineName ?? 'Meter Values'}
          </SheetTitle>
          <SheetDescription className="text-sm text-[#606060] space-y-0.5">
            <span>#{item?.machineId} · Connector {item?.connectorId}</span>
            {item && (
              <span className="block text-xs">
                Triggered by Meter Value <span className="font-mono">#{item.toMeterValueId}</span>
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        {/* Action buttons */}
        {item && visibleActions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap px-6 py-3 border-b border-[#E8E8E8] bg-white">
            <span className="text-xs text-[#606060] font-medium mr-1">Action:</span>
            {visibleActions.map(b => (
              <Button
                key={b.type}
                size="sm"
                variant="outline"
                disabled={isActioning}
                className={cn('h-7 text-xs gap-1.5', b.cls)}
                onClick={() => onHandle(item.irregularId, b.type)}
              >
                {b.icon}
                {b.label}
              </Button>
            ))}
            {item.handled && (
              <span className="ml-2 text-xs text-[#606060]">
                Handled by {item.handledByName ?? '—'}
              </span>
            )}
          </div>
        )}

        {/* Smart suggestion */}
        {suggestion && item && (
          <SuggestionCard
            suggestion={suggestion}
            onApply={() => onHandle(item.irregularId, suggestion.action)}
            disabled={isActioning}
          />
        )}

        {/* Surround size toggle + export */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-[#E8E8E8] bg-[#FAFAFA]">
          <span className="text-xs text-[#606060] font-medium">Surround size:</span>
          {SURROUND_OPTIONS.map(n => (
            <button
              key={n}
              onClick={() => onSurroundSizeChange(n)}
              className={cn(
                'h-7 w-7 rounded text-xs font-medium transition-colors',
                n === surroundSize
                  ? 'bg-[#FFCD00] text-[#262626]'
                  : 'bg-gray-100 text-gray-700 hover:bg-[#FFFBEB]'
              )}
            >
              {n}
            </button>
          ))}
          {meterValues.length > 0 && (
            <button
              onClick={handleExportMeterValues}
              title="Export meter values to Excel"
              className="ml-auto flex items-center gap-1 text-xs text-[#606060] border border-[#E8E8E8] rounded px-2 py-1 bg-white hover:border-[#FFCD00] hover:text-[#262626] transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Export xlsx
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-[#FFCD00] animate-spin" />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={onRetry}
                className="text-xs text-[#606060] underline hover:text-[#262626]"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && meterValues.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-[#606060]">No meter values found</p>
            </div>
          )}

          {!isLoading && !error && meterValues.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-max table-auto text-sm border-separate border-spacing-0 border border-[#E8E8E8] rounded">
                <thead>
                  {table.getHeaderGroups().map(hg => (
                    <tr key={hg.id}>
                      {hg.headers.map(header => (
                        <th
                          key={header.id}
                          className={cn(
                            'px-3 py-2 text-left text-xs font-medium whitespace-nowrap border-b border-[#E8E8E8]',
                            GRID_STYLES.header.background,
                            GRID_STYLES.header.text
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => {
                    const isTriggering = item && row.original.meterValueId === item.toMeterValueId;
                    return (
                      <tr
                        key={row.id}
                        className={cn(
                          'border-b border-[#E8E8E8] last:border-0',
                          isTriggering
                            ? 'bg-red-50 border-l-4 border-l-red-400'
                            : cn(GRID_STYLES.row.default, GRID_STYLES.row.hover)
                        )}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className="px-3 py-2 align-middle whitespace-nowrap"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
